import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useI18n } from '../../i18n';
import { setPageMeta } from './pageMeta';
import PhotoViewer from './PhotoViewer';

const previewCopy = {
  en: {
    metaTitle: 'Private creator preview | Outfit Detective',
    metaDescription: 'Private Outfit Detective creator preview.',
    loading: 'Loading...',
    unavailableTitle: 'Private preview unavailable',
    unavailableCopy: 'This private link is missing, expired, or no longer active.',
    openApp: 'Open Outfit Detective',
    closeImage: 'Close image',
    kicker: 'Private creator preview',
    title: (name) => `Fit check for ${name}`,
    fallbackCopy: 'Outfit Detective is still in beta: it takes inspiration from public looks and turns them into private fit checks. This private demo shows how a public look idea can be reimagined on one of your public posts.',
    privatePreview: 'Private preview',
    referencesTitle: 'Original references',
    originalPost: 'Your original post',
    sourcePost: 'Public reference look',
    sourceHint: 'Reference outfit used for this private preview',
    notesTitle: 'Notes from Outfit Detective',
    permissionKicker: 'Private demo',
    permissionTitle: 'This will not be published',
    permissionCopy: 'This preview is only here to show the idea. It will never be published on Outfit Detective or listed in search. It is removed automatically after 15 days. If you prefer, you can request immediate removal now.',
    remove: 'Request removal',
    removeFormTitle: 'Request removal',
    removeFormCopy: 'Choose the closest reason. A short note is optional.',
    reasonLabel: 'Reason',
    noteLabel: 'Note, optional',
    notePlaceholder: 'Add a short note',
    cancel: 'Cancel',
    submitRemoval: 'Send request',
    removalReasons: {
      not_comfortable: 'I prefer not to be included',
      wrong_photo: 'I do not want this photo used',
      not_me_or_inaccurate: 'This preview does not feel right',
      other: 'Other',
    },
    removedTitle: 'Removal requested',
    removedCopy: 'Request received. This preview is no longer active from the private link; we will complete the removal and send a confirmation email.',
  },
  it: {
    metaTitle: 'Preview creator privata | Outfit Detective',
    metaDescription: 'Preview creator privata di Outfit Detective.',
    loading: 'Caricamento...',
    unavailableTitle: 'Preview privata non disponibile',
    unavailableCopy: 'Questo link privato non esiste, e scaduto o non e piu attivo.',
    openApp: 'Apri Outfit Detective',
    closeImage: 'Chiudi immagine',
    kicker: 'Preview creator privata',
    title: (name) => `Fit check per ${name}`,
    fallbackCopy: 'Outfit Detective e un sito ancora in beta: prende ispirazione dai look pubblici e li trasforma in fit check privati. Questa demo privata mostra come un idea look pubblica puo essere reinterpretata su uno dei tuoi post pubblici.',
    privatePreview: 'Preview privata',
    referencesTitle: 'Riferimenti originali',
    originalPost: 'Il tuo post originale',
    sourcePost: 'Look pubblico di riferimento',
    sourceHint: 'Outfit di riferimento usato per questa preview privata',
    notesTitle: 'Note da Outfit Detective',
    permissionKicker: 'Demo privata',
    permissionTitle: 'Non sara pubblicata',
    permissionCopy: 'Questa preview ha solo finalita dimostrativa. Non sara mai pubblicata su Outfit Detective e non sara indicizzata. Viene cancellata automaticamente dopo 15 giorni. Se preferisci, puoi chiedere la rimozione immediata adesso.',
    remove: 'Chiedi rimozione',
    removeFormTitle: 'Richiesta rimozione',
    removeFormCopy: 'Scegli il motivo piu vicino. Una nota breve e opzionale.',
    reasonLabel: 'Motivo',
    noteLabel: 'Nota, opzionale',
    notePlaceholder: 'Aggiungi una nota breve',
    cancel: 'Annulla',
    submitRemoval: 'Invia richiesta',
    removalReasons: {
      not_comfortable: 'Preferisco non essere incluso/a',
      wrong_photo: 'Non voglio che questa foto venga usata',
      not_me_or_inaccurate: 'Questa preview non mi rappresenta',
      other: 'Altro',
    },
    removedTitle: 'Rimozione richiesta',
    removedCopy: 'Richiesta ricevuta. Questa preview non e piu attiva dal link privato; completeremo la rimozione e ti manderemo una conferma via email.',
  },
  ro: {
    metaTitle: 'Preview privata creator | Outfit Detective',
    metaDescription: 'Preview privata Outfit Detective pentru creator.',
    loading: 'Se incarca...',
    unavailableTitle: 'Preview privata indisponibila',
    unavailableCopy: 'Acest link privat lipseste, a expirat sau nu mai este activ.',
    openApp: 'Deschide Outfit Detective',
    closeImage: 'Inchide imaginea',
    kicker: 'Preview privata creator',
    title: (name) => `Fit check pentru ${name}`,
    fallbackCopy: 'Outfit Detective este inca in beta: ia inspiratie din lookuri publice si o transforma in fit check-uri private. Aceasta demo privata arata cum o idee de look publica poate fi reinterpretata pe una dintre postarile tale publice.',
    privatePreview: 'Preview privata',
    referencesTitle: 'Referinte originale',
    originalPost: 'Postarea ta originala',
    sourcePost: 'Look public de referinta',
    sourceHint: 'Outfit de referinta folosit pentru aceasta preview privata',
    notesTitle: 'Note de la Outfit Detective',
    permissionKicker: 'Demo privata',
    permissionTitle: 'Nu va fi publicata',
    permissionCopy: 'Aceasta preview are doar scop demonstrativ. Nu va fi publicata pe Outfit Detective si nu va fi indexata. Este stearsa automat dupa 15 zile. Daca preferi, poti cere eliminarea imediata acum.',
    remove: 'Cere eliminarea',
    removeFormTitle: 'Cere eliminarea',
    removeFormCopy: 'Alege motivul cel mai apropiat. O nota scurta este optionala.',
    reasonLabel: 'Motiv',
    noteLabel: 'Nota, optional',
    notePlaceholder: 'Adauga o nota scurta',
    cancel: 'Anuleaza',
    submitRemoval: 'Trimite cererea',
    removalReasons: {
      not_comfortable: 'Prefer sa nu fiu inclus/a',
      wrong_photo: 'Nu vreau ca aceasta poza sa fie folosita',
      not_me_or_inaccurate: 'Acest preview nu ma reprezinta',
      other: 'Altul',
    },
    removedTitle: 'Eliminare solicitata',
    removedCopy: 'Cerere primita. Acest preview nu mai este activ din linkul privat; vom finaliza eliminarea si iti vom trimite confirmarea pe email.',
  },
};

previewCopy.de = {
  ...previewCopy.en,
  metaTitle: 'Private Creator-Preview | Outfit Detective',
  unavailableTitle: 'Private Preview nicht verfugbar',
  unavailableCopy: 'Dieser private Link fehlt, ist abgelaufen oder nicht mehr aktiv.',
  openApp: 'Outfit Detective offnen',
  closeImage: 'Bild schliessen',
  kicker: 'Private Creator-Preview',
  title: (name) => `Fit-Check fur ${name}`,
  fallbackCopy: 'Outfit Detective ist noch in Beta: Es nimmt Inspiration aus offentlichen Looks und verwandelt sie in private Fit-Checks. Diese private Demo zeigt, wie eine offentliche Look-Idee auf einem deiner offentlichen Posts neu interpretiert werden kann.',
  privatePreview: 'Private Preview',
  referencesTitle: 'Original references',
  originalPost: 'Dein Original-Post',
  sourcePost: 'Offentlicher Referenz-Look',
  sourceHint: 'Referenz-Outfit fur diese private Preview',
  notesTitle: 'Notizen von Outfit Detective',
  permissionKicker: 'Private Demo',
  permissionTitle: 'Sie wird nicht veroffentlicht',
  permissionCopy: 'Diese Preview dient nur zur Demonstration. Sie wird nie auf Outfit Detective veroffentlicht und nicht indexiert. Sie wird nach 15 Tagen automatisch geloscht. Wenn du mochtest, kannst du sie jetzt sofort entfernen lassen.',
  remove: 'Entfernung anfordern',
  removeFormTitle: 'Entfernung anfordern',
  removeFormCopy: 'Wahle den passenden Grund. Eine kurze Notiz ist optional.',
  reasonLabel: 'Grund',
  noteLabel: 'Notiz, optional',
  notePlaceholder: 'Kurze Notiz hinzufugen',
  cancel: 'Abbrechen',
  submitRemoval: 'Anfrage senden',
  removalReasons: {
    not_comfortable: 'Ich mochte nicht aufgenommen werden',
    wrong_photo: 'Ich mochte nicht, dass dieses Foto verwendet wird',
    not_me_or_inaccurate: 'Diese Preview passt fur mich nicht',
    other: 'Anderer Grund',
  },
  removedTitle: 'Entfernung angefordert',
  removedCopy: 'Anfrage erhalten. Diese Preview ist uber den privaten Link nicht mehr aktiv; wir schliessen die Entfernung ab und senden dir eine Bestatigung per E-Mail.',
};

previewCopy.fr = {
  ...previewCopy.en,
  metaTitle: 'Preview createur privee | Outfit Detective',
  unavailableTitle: 'Preview privee indisponible',
  unavailableCopy: 'Ce lien prive est manquant, expire ou n est plus actif.',
  openApp: 'Ouvrir Outfit Detective',
  closeImage: 'Fermer l image',
  kicker: 'Preview createur privee',
  title: (name) => `Fit check pour ${name}`,
  fallbackCopy: 'Outfit Detective est encore en beta: le site prend inspiration de looks publics et les transforme en fit checks prives. Cette demo privee montre comment une idee look publique peut etre reinterpretee sur un de tes posts publics.',
  privatePreview: 'Preview privee',
  referencesTitle: 'References originales',
  originalPost: 'Ton post original',
  sourcePost: 'Look public de reference',
  sourceHint: 'Outfit de reference utilise pour cette preview privee',
  notesTitle: 'Notes de Outfit Detective',
  permissionKicker: 'Demo privee',
  permissionTitle: 'Elle ne sera pas publiee',
  permissionCopy: 'Cette preview a uniquement un but demonstratif. Elle ne sera jamais publiee sur Outfit Detective et ne sera pas indexee. Elle sera supprimee automatiquement apres 15 jours. Si tu preferes, tu peux demander sa suppression immediate maintenant.',
  remove: 'Demander la suppression',
  removeFormTitle: 'Demander la suppression',
  removeFormCopy: 'Choisis la raison la plus proche. Une courte note est optionnelle.',
  reasonLabel: 'Raison',
  noteLabel: 'Note, optionnelle',
  notePlaceholder: 'Ajouter une courte note',
  cancel: 'Annuler',
  submitRemoval: 'Envoyer la demande',
  removalReasons: {
    not_comfortable: 'Je prefere ne pas etre inclus/e',
    wrong_photo: 'Je ne veux pas que cette photo soit utilisee',
    not_me_or_inaccurate: 'Cette preview ne me correspond pas',
    other: 'Autre',
  },
  removedTitle: 'Suppression demandee',
  removedCopy: 'Demande recue. Cette preview n est plus active depuis le lien prive; nous finaliserons la suppression et t enverrons une confirmation par email.',
};

previewCopy.es = {
  ...previewCopy.en,
  metaTitle: 'Preview privada de creator | Outfit Detective',
  unavailableTitle: 'Preview privada no disponible',
  unavailableCopy: 'Este enlace privado no existe, ha caducado o ya no esta activo.',
  openApp: 'Abrir Outfit Detective',
  closeImage: 'Cerrar imagen',
  kicker: 'Preview privada de creator',
  title: (name) => `Fit check para ${name}`,
  fallbackCopy: 'Outfit Detective todavia esta en beta: toma inspiracion de looks publicos y la convierte en fit checks privados. Esta demo privada muestra como una idea de look publica puede reinterpretarse en uno de tus posts publicos.',
  privatePreview: 'Preview privada',
  referencesTitle: 'Referencias originales',
  originalPost: 'Tu post original',
  sourcePost: 'Look publico de referencia',
  sourceHint: 'Outfit de referencia usado para esta preview privada',
  notesTitle: 'Notas de Outfit Detective',
  permissionKicker: 'Demo privada',
  permissionTitle: 'No se publicara',
  permissionCopy: 'Esta preview tiene solo una finalidad demostrativa. Nunca se publicara en Outfit Detective ni sera indexada. Se elimina automaticamente despues de 15 dias. Si lo prefieres, puedes solicitar la eliminacion inmediata ahora.',
  remove: 'Solicitar eliminacion',
  removeFormTitle: 'Solicitar eliminacion',
  removeFormCopy: 'Elige el motivo mas cercano. Una nota breve es opcional.',
  reasonLabel: 'Motivo',
  noteLabel: 'Nota, opcional',
  notePlaceholder: 'Anade una nota breve',
  cancel: 'Cancelar',
  submitRemoval: 'Enviar solicitud',
  removalReasons: {
    not_comfortable: 'Prefiero no ser incluido/a',
    wrong_photo: 'No quiero que se use esta foto',
    not_me_or_inaccurate: 'Esta preview no me representa',
    other: 'Otro',
  },
  removedTitle: 'Eliminacion solicitada',
  removedCopy: 'Solicitud recibida. Esta preview ya no esta activa desde el enlace privado; completaremos la eliminacion y te enviaremos una confirmacion por email.',
};

function creatorPreviewText(language) {
  return previewCopy[language] || previewCopy.en;
}

export default function CreatorPreview() {
  const { token } = useParams();
  const { t } = useI18n();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoViewer, setPhotoViewer] = useState({ open: false, sources: [], initialSlide: 0 });
  const [isRemovalOpen, setIsRemovalOpen] = useState(false);
  const [removalReason, setRemovalReason] = useState('not_comfortable');
  const [removalNote, setRemovalNote] = useState('');
  const copy = creatorPreviewText(preview?.preview_language);

  useEffect(() => {
    const metaCopy = creatorPreviewText(preview?.preview_language);
    setPageMeta({
      title: metaCopy.metaTitle,
      description: metaCopy.metaDescription,
      pathname: `/creator-preview/${token}`,
      robots: 'noindex,nofollow',
    });
  }, [token, preview?.preview_language]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.getCreatorPreview(token)
      .then((response) => {
        if (mounted) setPreview(response.preview || null);
      })
      .catch((err) => {
        if (mounted) setError(err.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  async function requestRemoval() {
    setSaving(true);
    setError('');
    try {
      await api.removeCreatorPreview(token, {
        reason: removalReason,
        note: removalNote,
      });
      setDone('remove');
      setIsRemovalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openPhotoViewer(sources, initialSlide = 0) {
    const validSources = (Array.isArray(sources) ? sources : [sources]).filter(Boolean);
    if (!validSources.length) return;
    setPhotoViewer({ open: true, sources: validSources, initialSlide });
  }

  if (loading) {
    return (
      <main className="ct-public-tryon-page">
        <div className="ct-card ct-empty">{t('common.loading')}</div>
      </main>
    );
  }

  if (error || !preview) {
    return (
      <main className="ct-public-tryon-page">
        <section className="ct-public-tryon-missing">
          <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          <h1>{copy.unavailableTitle}</h1>
          <p>{copy.unavailableCopy}</p>
          <Link to="/" className="btn btn-dark">{copy.openApp}</Link>
        </section>
      </main>
    );
  }

  const hasOriginalReference = Boolean(preview.reference_image_url || preview.reference_title || preview.reference_url);
  const hasSourceReference = Boolean(preview.source_event_image_url || preview.source_event_title || preview.source_event_id);
  const photoViewerSources = [
    preview.preview_image_url || preview.reference_image_url,
    preview.reference_image_url,
    preview.source_event_image_url,
  ].filter(Boolean);

  return (
    <main className="ct-public-tryon-page">
      <PhotoViewer
        open={photoViewer.open}
        sources={photoViewer.sources}
        initialSlide={photoViewer.initialSlide}
        onClose={() => setPhotoViewer({ open: false, sources: [], initialSlide: 0 })}
        t={t}
      />

      <section className="ct-public-tryon-shell ct-public-tryon-shell-detail">
        <Link to="/" className="ct-text-link">Outfit Detective</Link>

        <article className="ct-user-fit-detail">
          <button
            type="button"
            className="ct-user-fit-media"
            onClick={() => openPhotoViewer(photoViewerSources, 0)}
            disabled={!preview.preview_image_url && !preview.reference_image_url}
          >
            {preview.preview_image_url || preview.reference_image_url ? (
              <img src={preview.preview_image_url || preview.reference_image_url} alt="" />
            ) : (
              <div className="ct-empty">{copy.privatePreview}</div>
            )}
          </button>

          <aside className="ct-user-fit-panel">
            <div className="ct-user-fit-copy">
              <p className="ct-kicker">{copy.kicker}</p>
              <h1>{preview.preview_title || copy.title(preview.creator_name)}</h1>
              <p>
                {preview.preview_copy ||
                  copy.fallbackCopy}
              </p>
            </div>

            {hasOriginalReference || hasSourceReference ? (
              <section className="ct-creator-preview-references" aria-label={copy.referencesTitle}>
                <strong>{copy.referencesTitle}</strong>

                {hasOriginalReference ? (
                  preview.reference_image_url ? (
                    <button
                      className="ct-user-fit-original"
                      type="button"
                      onClick={() => openPhotoViewer(photoViewerSources, Math.max(photoViewerSources.indexOf(preview.reference_image_url), 0))}
                    >
                      <img src={preview.reference_image_url} alt="" />
                      <span>
                        <small>{copy.originalPost}</small>
                        <strong>{preview.reference_title || copy.originalPost}</strong>
                        <em>{preview.platform}{preview.creator_handle ? ` @${preview.creator_handle}` : ''}</em>
                      </span>
                    </button>
                  ) : (
                    <div className="ct-user-fit-original ct-user-fit-original-static">
                      <span className="ct-user-fit-original-icon"><i className="ph ph-instagram-logo"></i></span>
                      <span>
                        <small>{copy.originalPost}</small>
                        <strong>{preview.reference_title || preview.reference_url || copy.originalPost}</strong>
                        <em>{preview.platform}{preview.creator_handle ? ` @${preview.creator_handle}` : ''}</em>
                      </span>
                    </div>
                  )
                ) : null}

                {hasSourceReference ? (
                  preview.source_event_image_url ? (
                    <button
                      className="ct-user-fit-original ct-user-fit-source"
                      type="button"
                      onClick={() => openPhotoViewer(photoViewerSources, Math.max(photoViewerSources.indexOf(preview.source_event_image_url), 0))}
                    >
                      <img src={preview.source_event_image_url} alt="" />
                      <span>
                        <small>{copy.sourcePost}</small>
                        <strong>{preview.source_event_title || copy.sourcePost}</strong>
                        <em>{copy.sourceHint}</em>
                      </span>
                    </button>
                  ) : (
                    <div className="ct-user-fit-original ct-user-fit-original-static ct-user-fit-source">
                      <span className="ct-user-fit-original-icon"><i className="ph ph-sparkle"></i></span>
                      <span>
                        <small>{copy.sourcePost}</small>
                        <strong>{preview.source_event_title || `${copy.sourcePost} #${preview.source_event_id}`}</strong>
                        <em>{copy.sourceHint}</em>
                      </span>
                    </div>
                  )
                ) : null}
              </section>
            ) : null}

            {preview.product_notes ? (
              <section className="ct-public-selected-products ct-user-fit-products">
                <strong>{copy.notesTitle}</strong>
                <p>{preview.product_notes}</p>
              </section>
            ) : null}

            {done === 'remove' ? (
              <section className="ct-user-fit-cta">
                <h2>{copy.removedTitle}</h2>
                <p>{copy.removedCopy}</p>
              </section>
            ) : isRemovalOpen ? (
              <section className="ct-user-fit-cta ct-creator-removal-form">
                <div>
                  <p className="ct-kicker">{copy.permissionKicker}</p>
                  <h2>{copy.removeFormTitle}</h2>
                </div>
                <p>{copy.removeFormCopy}</p>
                <label>
                  <span>{copy.reasonLabel}</span>
                  <select value={removalReason} onChange={(event) => setRemovalReason(event.target.value)}>
                    {Object.entries(copy.removalReasons).map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>{copy.noteLabel}</span>
                  <textarea
                    value={removalNote}
                    onChange={(event) => setRemovalNote(event.target.value)}
                    placeholder={copy.notePlaceholder}
                    rows={3}
                    maxLength={1000}
                  />
                </label>
                <div className="ct-public-tryon-cta-actions">
                  <button className="btn btn-light" disabled={saving} onClick={() => setIsRemovalOpen(false)} type="button">{copy.cancel}</button>
                  <button className="btn btn-dark" disabled={saving} onClick={requestRemoval} type="button">{copy.submitRemoval}</button>
                </div>
              </section>
            ) : (
              <section className="ct-user-fit-cta">
                <div>
                  <p className="ct-kicker">{copy.permissionKicker}</p>
                  <h2>{copy.permissionTitle}</h2>
                </div>
                <p>{copy.permissionCopy}</p>
                <div className="ct-public-tryon-cta-actions">
                  <button className="btn btn-light" disabled={saving} onClick={() => setIsRemovalOpen(true)} type="button">{copy.remove}</button>
                </div>
              </section>
            )}
          </aside>
        </article>
      </section>
    </main>
  );
}
